import IAuthStrategy from "./IAuthStrategy.js";
import { tokenStorage } from "@/shared/auth/tokenStorage";

export default class NclayerAuthStrategy extends IAuthStrategy {
  constructor(authPort, storagePort, mode) {
    super();
    this.authPort = authPort;
    this.storagePort = storagePort;
    this.mode = mode;
  }

  /**
   * Этап 1: challenge → подпись nonce → login/init или login/registration
   * Возвращает: { _success, session_id, roles }
   *
   * Безопасность: подписывается серверный nonce (challenge-response).
   * Replay невозможен — nonce one-shot и имеет TTL.
   */
  async login() {
    try {
      const action = this.mode === "registration" ? "register" : "login";

      // 1) Получаем challenge с сервера
      const challenge = await this.authPort.getChallenge(action);
      const challengeFetchedAt = Date.now();

      // 2) Подписываем nonce через NCALayer
      const signed = await this.authPort.sign(challenge.nonce);

      const signatures = signed.signatures;
      if (!signatures || signatures.length === 0) {
        throw new Error("Не удалось подписать данные ЭЦП");
      }

      // 3) TTL guard: подписание через NCALayer могло занять много времени
      const expiresInMs = (challenge.expires_in || 0) * 1000;
      if (
        expiresInMs > 0 &&
        Date.now() - challengeFetchedAt >= expiresInMs - 5000
      ) {
        throw new Error("CHALLENGE_EXPIRED");
      }

      // 4) Отправляем /login/init или /login/registration/ с nonce в body
      const initRes = await this.authPort.login(signatures[0], this.mode, {
        nonce: challenge.nonce,
      });

      // initRes = { _success, session_id, roles }
      return initRes;
    } catch (err) {
      console.error("Ошибка NclayerAuthStrategy.login():", err);
      return {
        _success: false,
        message: err.message || this.$t("Ошибка авторизации через ЭЦП"),
      };
    }
  }

  /**
   * Этап 2: Подтверждение роли
   * Вызывает: /login/confirm/
   * Возвращает: токены
   */
  async confirm(session_id, role_code) {
    try {
      const res = await this.authPort.confirm(session_id, role_code);

      if (res._success) {
        tokenStorage.setFromConfirm(res);
      }

      return res;
    } catch (err) {
      console.error("Ошибка NclayerAuthStrategy.confirm():", err);
      return {
        _success: false,
        message: err.message || this.$t("Ошибка подтверждения роли"),
      };
    }
  }

  /**
   * Logout — полностью очистить хранилище
   */
  async logout() {
    // Серверный logout: бэк помечает session_id revoked и сбрасывает
    // refresh-cookie. Не блокируем UX на ошибке — фронтовая зачистка важнее.
    try {
      await this.authPort.serverLogout();
    } catch (e) {
      console.warn(
        "Server logout failed, clearing client-side only:",
        e?.message
      );
    }
    tokenStorage.clear();
    this.clearSessionKeys(["redirectPath"]);
    window.location.reload();
  }

  /**
   * Обновление access-токена. Refresh-token летит автоматически в HttpOnly
   * cookie (см. NclayerAuthPort.refreshTokens — body пустое).
   */
  async refreshTokens() {
    try {
      const res = await this.authPort.refreshTokens();
      if (res?._success && res.access_token) {
        tokenStorage.setAccessToken(res.access_token);
      }
      return res;
    } catch (error) {
      console.error("Ошибка обновления токенов:", error);
      return {
        _success: false,
        message: this.$t("Ошибка обновления токенов"),
      };
    }
  }

  clearSessionKeys(keys = []) {
    keys.forEach((key) => sessionStorage.removeItem(key));
  }
}
